/**
 * Which child each conference is for.
 *
 * A parent can bring up to MAX_CHILDREN kids to one sign-up. Each teacher or
 * team card carries a checkbox per kid; ticking two kids on one teacher means
 * two separate meetings with that teacher, so the scheduler reserves two slots.
 *
 * The map is sheetId -> childId[]. Older saved data (localStorage, shared
 * links) stored a single childId string per sheet — `childIdsFor` reads both.
 */

export const MAX_CHILDREN = 3;

export interface Child {
  id: string;
  name: string;
}

/** sheetId -> childIds. A plain string is the legacy single-child shape. */
export type TeacherChildMap = Record<string, string[] | string>;

export function childIdsFor(map: TeacherChildMap | null | undefined, sheetId: string): string[] {
  const v = map?.[sheetId];
  if (!v) return [];
  if (typeof v === 'string') return [v];
  return v.filter((id): id is string => typeof id === 'string' && id.length > 0);
}

/** Coerce anything parsed from storage or a URL into the array shape. */
export function normalizeChildMap(raw: unknown): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const [sheetId, v] of Object.entries(raw as Record<string, unknown>)) {
    const ids = typeof v === 'string' ? [v] : Array.isArray(v) ? v.filter((x) => typeof x === 'string') : [];
    if (ids.length) out[sheetId] = ids as string[];
  }
  return out;
}

export function toggleChildForSheet(map: TeacherChildMap, sheetId: string, childId: string): Record<string, string[]> {
  const next = normalizeChildMap(map);
  const current = next[sheetId] || [];
  const updated = current.includes(childId) ? current.filter((id) => id !== childId) : [...current, childId];
  if (updated.length) next[sheetId] = updated;
  else delete next[sheetId];
  return next;
}

/** True when this sheet is booked for more than one child, which the server
 * must be told (`secondChild`) so its one-conference-per-teacher rule allows it. */
export function needsSecondChildFlag(map: TeacherChildMap | null | undefined, sheetId: string): boolean {
  return childIdsFor(map, sheetId).length > 1;
}

interface GroupLike {
  id: string;
  sheets: any[];
  displayName: string;
}

export type ChildGroup<G extends GroupLike> = G & {
  /** The teacher/team group this came from, before splitting per child. */
  baseId: string;
  childId?: string;
  childName?: string;
};

/**
 * Split every teacher/team group into one scheduling unit per assigned child.
 * A group with no child assigned stays a single unit, so pages keep working
 * for families that skipped the kids step.
 */
export function expandGroupsForChildren<G extends GroupLike>(
  groups: G[],
  map: TeacherChildMap | null | undefined,
  children: Child[] | null | undefined,
): ChildGroup<G>[] {
  const kids = children || [];
  const out: ChildGroup<G>[] = [];
  for (const g of groups) {
    const assigned = new Set<string>();
    for (const sheet of g.sheets) for (const id of childIdsFor(map, sheet._id)) assigned.add(id);
    const ordered = kids.filter((k) => assigned.has(k.id));
    if (!ordered.length) {
      out.push({ ...g, baseId: g.id });
      continue;
    }
    for (const kid of ordered) {
      const label = kid.name?.trim() || `Child ${kids.indexOf(kid) + 1}`;
      out.push({
        ...g,
        id: `${g.id}::${kid.id}`,
        baseId: g.id,
        childId: kid.id,
        childName: label,
        displayName: kids.length > 1 ? `${g.displayName} · ${label}` : g.displayName,
      });
    }
  }
  return out;
}

/**
 * Read the `children` URL param the school page hands to the planner. Used
 * as a synchronous state initializer so the very first render already has the
 * per-child units — an effect would run one render late, after auto-suggest
 * had keyed its picks by the unsplit teacher groups.
 */
/** What the parent told us on the registration page about when they can come. */
export interface SchedulingPreferences {
  /** "HH:mm" in the school's timezone, or "" for no preference. */
  startTime: string;
  /** yyyy-MM-dd conference days the parent can attend; empty = any day. */
  dates: string[];
}

export const EMPTY_PREFERENCES: SchedulingPreferences = { startTime: '', dates: [] };

export function normalizePreferences(raw: unknown): SchedulingPreferences {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_PREFERENCES };
  const r = raw as any;
  const startTime = typeof r.startTime === 'string' && /^\d{2}:\d{2}$/.test(r.startTime) ? r.startTime : '';
  const dates = Array.isArray(r.dates) ? r.dates.filter((d: unknown) => typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) : [];
  return { startTime, dates: [...new Set(dates as string[])].sort() };
}

export function readChildDataFromSearch(search: string | null | undefined): { children: Child[]; teacherChildMap: Record<string, string[]>; preferences: SchedulingPreferences } {
  const empty = { children: [] as Child[], teacherChildMap: {} as Record<string, string[]>, preferences: { ...EMPTY_PREFERENCES } };
  if (!search) return empty;
  try {
    const raw = new URLSearchParams(search).get('children');
    if (!raw) return empty;
    const data = JSON.parse(decodeURIComponent(raw));
    const kids: Child[] = Array.isArray(data?.children)
      ? data.children.filter((c: any) => c && typeof c.id === 'string').map((c: any) => ({ id: c.id, name: String(c.name || '') }))
      : [];
    return { children: kids, teacherChildMap: normalizeChildMap(data?.teacherChildMap), preferences: normalizePreferences(data?.preferences) };
  } catch {
    return empty;
  }
}
